import {cn} from '@/lib/utils';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import {cva} from 'class-variance-authority';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {Button} from '@/components/ui/button';

export type CustomToastParams = {
  title?: string;
  description?: string;
  duration?: number;
  action?: CustomToastActions;
  toastId?: any;
  hideCloseButton?: boolean;
  variant?: 'default' | 'success' | 'destructive';
  correlationId?: string;
  timestamp?: string;
  id?: string;
};

export type CustomToastActions = {
  label: string;
  onClick?: (event) => void;
  path?: string;
};

const sonnerVariants = cva(
  'w-[380px] group flex-col gap-4 pointer-events-auto relative flex justify-between space-x-4 overflow-hidden border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'border-muted-foreground bg-background text-muted-foreground',
        success:
          'success-alert group border-success-alert-foreground bg-success-alert text-success-alert-foreground',
        destructive:
          'destructive group border-destructive bg-destructive text-destructive-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export function CustomToast({params, className}: {params: CustomToastParams; className: string}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  return (
    <div className={cn(sonnerVariants({variant: params.variant}), className)}>
      <div className="flex items-start">
        {params.variant === 'success' ? (
          <CheckCircleOutlineIcon className="text-sucess-item-foreground" />
        ) : params.variant === 'destructive' ? (
          <ReportGmailerrorredIcon />
        ) : (
          <></>
        )}

        {params.title ? (
          <div className="flex flex-col">
            {params.title.split(';').map((title, index) => (
              <h1 key={index} className="ml-4 font-semibold">
                {t(title)}
              </h1>
            ))}
          </div>
        ) : (
          <></>
        )}

        {params.hideCloseButton ? (
          <></>
        ) : (
          <button onClick={() => toast.dismiss(params.toastId)} className="ml-auto">
            <CloseIcon />
          </button>
        )}
      </div>
      <div className="!ml-0 flex items-center">
        <div
          className={`flex-1 p-0 text-sm ${
            params.variant === 'default' || !params.variant ? 'ml-4' : 'ml-10'
          }`}
        >
          {t(params.description)}
          {params.variant === 'destructive' && params.correlationId && (
            <div className="mt-2 text-xs">
              <div><strong>{t('Error.ReferenceId')}:</strong> {params.correlationId}</div>
            </div>
          )}
        </div>
        {params.action ? (
          <Button
            variant={params.variant}
            onClick={(e) => {
              if (params.action.onClick) params.action.onClick(e);
              if (params.action.path) navigate(params.action.path);
              toast.dismiss(params.toastId);
            }}
            className="w-max self-end"
          >
            {t(params.action.label)}
          </Button>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}

export function CustomSimpleErrorToast({params, className}: {params: CustomToastParams; className: string}) {
  const {t} = useTranslation();
  
  return (
    <div className={cn(sonnerVariants({ variant: 'destructive' }), className)}>
      <div className="flex items-start">
        <ReportGmailerrorredIcon />

        {params.title ? (
          <div className="flex flex-col">
            {params.title.split(';').map((title, index) => (
              <h1 key={index} className="ml-4 font-semibold">
                {title}
              </h1>
            ))}
          </div>
        ) : (
          <></>
        )}
        
        {params.hideCloseButton ? (
          <></>
        ) : (
          <button onClick={() => toast.dismiss(params.toastId)} className="ml-auto">
            <CloseIcon />
          </button>
        )}
      </div>
      <div className="!ml-0 flex items-center">
        <div className="flex-1 p-0 text-sm ml-10">
          {params.description}
          {params.correlationId && (
            <div className="mt-2 text-xs">
              <div><strong>{t('Error.ReferenceId')}:</strong> {params.correlationId}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
